import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axiosInstance from "../utils/axiosInstance";
import { getPercentageChange } from "./NewBussinessChart";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";

const compact = (value: number) =>
  new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(value);

const ChartTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;

  return (
    <div className="rounded-xl bg-surface-elevated border border-edge shadow-overlay px-3 py-2">
      <p className="text-[11px] uppercase tracking-wider text-light-gray">{item?.month}</p>
      <p className="text-[15px] font-bold text-text-primary tabular-nums">
        {new Intl.NumberFormat("en-GB").format(item?.count ?? 0)}
      </p>
    </div>
  );
};

export default function TotalChases() {
  const [chartsData, setChartsData] = useState<Record<string, any>>()
  const [loading, setLoading] = useState<boolean>(false)
  const precentageData = getPercentageChange(chartsData?.data[chartsData?.data?.length - 1]?.count, chartsData?.data[chartsData?.data?.length - 2]?.count)

  useEffect(() => {
    const getChartsData = async () => {
      setLoading(true)
      try {
        const res = await axiosInstance.get(`/stats/chase/compare`)
        setChartsData(res.data)
        setLoading(false)
      } catch (error) {
        console.log(error)
        setLoading(false)
      }
    }

    getChartsData()
  }, [])


  if (loading) {
    return (
      <div className="w-full animate-pulse bg-surface border border-edge rounded-2xl h-full min-h-[300px]" />
    )
  }


  return (
    <div className="w-full h-full flex flex-col bg-surface border border-edge shadow-card rounded-2xl px-6 py-5 animate-fade-in smallTablet:px-4 smallTablet:min-w-0">
      <div className="flex items-start justify-between gap-4 smallTablet:flex-wrap smallTablet:gap-2">
        <div>
          <h1 className="text-[16px] font-bold text-text-primary">Total Chases</h1>
          <p className="mt-0.5 text-[13px] font-medium text-light-gray">Last 6 months</p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-[22px] font-bold text-text-primary tracking-tight leading-7 tabular-nums">
            {new Intl.NumberFormat("en-GB").format(chartsData?.totalCount ?? 0)}
          </p>
          <div
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tabular-nums bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
              precentageData < 0 && "!bg-rose-50 dark:!bg-rose-500/15 !text-rose-700 dark:!text-rose-300"
            )}
          >
            <FontAwesomeIcon
              icon={faArrowUp}
              className={clsx("text-[10px]", precentageData < 0 && "rotate-180")}
            />
            {precentageData >= 0 && "+"}
            {precentageData ? precentageData.toFixed(0) : 0}%
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-[180px] mt-5 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartsData?.data}
            margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
            barCategoryGap="45%"
          >
            <CartesianGrid vertical={false} stroke="var(--color-border-edge-faint)" />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tick={{ fill: "var(--color-light-gray)", fontSize: 11 }}
            />
            <YAxis
              width={44}
              tickLine={false}
              axisLine={false}
              tickCount={5}
              tickFormatter={compact}
              tick={{ fill: "var(--color-light-gray)", fontSize: 11 }}
            />
            <Tooltip cursor={{ fill: "var(--color-bg-overlay)" }} content={<ChartTooltip />} />
            <Bar
              dataKey="count"
              fill="#6366F1"
              isAnimationActive={false}
              radius={[6, 6, 6, 6]}
              maxBarSize={40}
              name="Chases"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
